import React, { useMemo } from 'react';

import useApi from '@/hooks/useApi';
import { useSlider } from '@/hooks/useSlider';
import useModal from '@/hooks/useModal';

import { ButtonsSlider } from '@/components/global/ButtonSlider';

import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionCardClick } from '@/services/requests/Interactions/types';
import {
  TCard,
  TRecommendation,
} from '@/services/requests/Recommendation/types/template4';

import { getVisibleCardsIdByElement } from '@/helpers/HtmlHelper/getVisibleCardsIdByElement';
import { getCTA } from '@/helpers/CtaHelper';
import { getValue } from '@/helpers/GetValueHelper';

import { Card } from './Card';
import { CardAereo } from './CardAereo';
import { ModalTitle } from './ModalCard/ModalTitle';
import { ModalContent } from './ModalCard/ModalContent';

type TProps = {
  data: TRecommendation;
  tabName: string;
  tabActive: boolean;
};

const TASK_COMPLETE = 'complete';

export const Slider = ({ data, tabName, tabActive }: TProps) => {
  const modal = useModal();

  const { id, cards } = data;

  const { Api } = useApi();
  const { sliderState, moveSlider, containerRef, checkScrollLimit } = useSlider(
    data,
    tabName,
    tabActive,
    data.layout,
    'vitrine-template4__card',
  );

  const handleCardClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    card: TCard,
  ) => {
    event.preventDefault();
    const cardId = card.id;

    await dispatchInteraction<TInteractionCardClick>(Api, 'card-click', {
      recommendationId: id,
      tabName,
      cardId,
      cardPosition: cards.findIndex(item => item.id === cardId) + 1,
      cards: getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'card-click',
      ),
    });

    if (
      card.task_status === TASK_COMPLETE &&
      card.info_modal.modal_title?.modal_title
    ) {
      modal.setContent(
        <ModalTitle data={card} />,
        <ModalContent data={card} />,
      );
      modal.setShow(true);

      return true;
    }

    const cta = getCTA(card.cta, 'web');
    const link = getValue(cta, 'link', '') as string;

    if (link) {
      window.location.assign(link);
    }

    return true;
  };

  const handleCardAreoClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    card: TCard,
  ) => {
    const cardId = card.id;

    await dispatchInteraction<TInteractionCardClick>(Api, 'card-click', {
      recommendationId: id,
      tabName,
      cardId,
      cardPosition: cards.findIndex(item => item.id === cardId) + 1,
      cards: getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'card-click',
      ),
    });
  };

  const isCardAereo = (card: TCard) => {
    return card.detail.current_value && card.detail.total_value;
  };

  const isShowButtonSlider = useMemo(() => {
    return cards.length > 2;
  }, []);

  return (
    <div className="vitrine-template4__slider">
      <>
        {isShowButtonSlider && (
          <ButtonsSlider
            handleClick={e => moveSlider(e)}
            sliderScroll={sliderState.scroll}
            checkScrollLimit={checkScrollLimit}
            template="4"
          />
        )}
        <div
          className="vitrine-template4__cards"
          ref={containerRef}
          data-testid="cards"
        >
          {cards.map(card =>
            isCardAereo(card) ? (
              <CardAereo
                key={card.id}
                data={card}
                onClick={e => handleCardAreoClick(e, card)}
              />
            ) : (
              <Card
                key={card.id}
                data={card}
                onClick={e => handleCardClick(e, card)}
              />
            ),
          )}
        </div>
      </>
    </div>
  );
};
